import {
  Box,
  Button,
  chakra,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Stack,
  Switch,
  Tag,
  TagLabel,
  Text,
  useToast,
  VStack,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { FormSelect, FormTextArea } from "../../../../components/Forms";
import { dimensionsKeys, filterActiveDimensionItems, generateArrayOfYears, QUESTIONNAIRE_STATUSES, showErrorToast, showSuccessToast } from "../../../../utils";
import { useForm } from "react-hook-form";
import {
  organizationAdministrationKeys,
  organizationQuestionnaireKeys,
  useCreateQuestionnaire,
  useGetOrganizationStructure,
  useGetOrganizationUsers,
  useGetQuestionnaireById,
  useUpdateQuestionnaire,
} from "../../../../hooks/query";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { hideAppSpinner, showAppSpinner } from "../../../../context/Store/slices/appSpinnerSlice";
import { useDispatch } from "react-redux";
import { useGetAssigneeTemplates, useGetDimensions } from "../../../../hooks/query/dimensions";
import EsgFormLabel from "../../../../components/Forms/EsgFormLabel";
import { useNavigate, useSearchParams } from "react-router-dom";
import { routes } from "../../../../config";
import { useQueryClient } from "react-query";
import LocalSpinner from "../../../../components/LocalSpinner";
import { useAuth } from "../../../../context";
import { sortUsersByEmail } from "../../../../utils/userSort";

const ALL_VALUE = "ALL";

const ScopeTag = ({ label, isSelected, onClick }) => (
  <WrapItem>
    <Tag
      size="lg"
      borderRadius="full"
      bg={isSelected ? "lightLime" : "gray.100"}
      border="1px solid"
      borderColor={isSelected ? "lime" : "gray.300"}
      color={isSelected ? "gray.800" : "gray.600"}
      fontWeight="600"
      px={4}
      py={2}
      cursor="pointer"
      onClick={onClick}
      _hover={{ borderColor: "lime" }}
    >
      <TagLabel fontSize="14px">{label}</TagLabel>
      <Box as="span" ml={2} fontSize="xs">{isSelected ? "✕" : "+"}</Box>
    </Tag>
  </WrapItem>
);

const SCOPE3_CATEGORIES = [
  { field: "purchasedGoodsAndServicesShouldBeFilled", label: "Cat 1 - Purchased Goods & Services" },
  { field: "capitalGoodsShouldBeFilled", label: "Cat 2 - Capital Goods" },
  { field: "fuelAndEnergyRelatedActivitiesShouldBeFilled", label: "Cat 3 - Fuel & Energy" },
  { field: "upstreamTransportationAndDistributionShouldBeFilled", label: "Cat 4 - Upstream Transport" },
  { field: "wasteGeneratedInOperationsShouldBeFilled", label: "Cat 5 - Waste" },
  { field: "businessTravelShouldBeFilled", label: "Cat 6 - Business Travel" },
  { field: "employeeCommutingShouldBeFilled", label: "Cat 7 - Employee Commuting" },
  { field: "upstreamLeasedAssetsShouldBeFilled", label: "Cat 8 - Upstream Leased Assets", disabled: true },
  { field: "downstreamTransportationAndDistributionShouldBeFilled", label: "Cat 9 - Downstream Transport" },
  { field: "processingOfSoldProductsShouldBeFilled", label: "Cat 10 - Processing of Sold Products", disabled: true },
  { field: "useOfSoldProductsShouldBeFilled", label: "Cat 11 - Use of Sold Products" },
  { field: "treatmentOfSoldProductsShouldBeFilled", label: "Cat 12 - End-of-Life Treatment" },
  { field: "downstreamLeasedAssetsShouldBeFilled", label: "Cat 13 - Downstream Leased Assets", disabled: true },
  { field: "franchisesShouldBeFilled", label: "Cat 14 - Franchises" },
  { field: "investmentsShouldBeFilled", label: "Cat 15 - Investments" },
];


function mapDataToFormData(data) {
  const assigneeId = data?.assigneeId ?? "";
  return {
    id: data?.id,
    description: data?.description ?? "",
    dimensionOne: data?.dimensionItems?.dimensionOneItem ?? ALL_VALUE,
    dimensionTwo: data?.dimensionItems?.dimensionTwoItem ?? ALL_VALUE,
    dimensionThree: data?.dimensionItems?.dimensionThreeItem ?? ALL_VALUE,
    dimensionFour: data?.dimensionItems?.dimensionFourItem ?? ALL_VALUE,
    dimensionFive: data?.dimensionItems?.dimensionFiveItem ?? ALL_VALUE,
    assignee: assigneeId,
    year: data?.period?.year ?? "",
    quarter: data?.period?.quarter ?? "",
    activityDataEnabled: data?.configurations?.activityDataEnabled ?? true,
    emissionsDataEnabled: data?.configurations?.emissionsDataEnabled ?? false,
    directEmissionsShouldBeFilled: data?.configurations?.directEmissionsShouldBeFilled ?? false,
    indirectEmissionsShouldBeFilled: data?.configurations?.indirectEmissionsShouldBeFilled ?? false,
    purchasedGoodsAndServicesShouldBeFilled: data?.configurations?.purchasedGoodsAndServicesShouldBeFilled ?? false,
    capitalGoodsShouldBeFilled: data?.configurations?.capitalGoodsShouldBeFilled ?? false,
    fuelAndEnergyRelatedActivitiesShouldBeFilled:
      data?.configurations?.fuelAndEnergyRelatedActivitiesShouldBeFilled ?? false,
    upstreamTransportationAndDistributionShouldBeFilled:
      data?.configurations?.upstreamTransportationAndDistributionShouldBeFilled ?? false,
    wasteGeneratedInOperationsShouldBeFilled: data?.configurations?.wasteGeneratedInOperationsShouldBeFilled ?? false,
    businessTravelShouldBeFilled: data?.configurations?.businessTravelShouldBeFilled ?? false,
    employeeCommutingShouldBeFilled: data?.configurations?.employeeCommutingShouldBeFilled ?? false,
    upstreamLeasedAssetsShouldBeFilled: data?.configurations?.upstreamLeasedAssetsShouldBeFilled ?? false,
    downstreamTransportationAndDistributionShouldBeFilled:
      data?.configurations?.downstreamTransportationAndDistributionShouldBeFilled ?? false,
    processingOfSoldProductsShouldBeFilled: data?.configurations?.processingOfSoldProductsShouldBeFilled ?? false,
    useOfSoldProductsShouldBeFilled: data?.configurations?.useOfSoldProductsShouldBeFilled ?? false,
    treatmentOfSoldProductsShouldBeFilled: data?.configurations?.treatmentOfSoldProductsShouldBeFilled ?? false,
    downstreamLeasedAssetsShouldBeFilled: data?.configurations?.downstreamLeasedAssetsShouldBeFilled ?? false,
    franchisesShouldBeFilled: data?.configurations?.franchisesShouldBeFilled ?? false,
    investmentsShouldBeFilled: data?.configurations?.investmentsShouldBeFilled ?? false,
  };
}

function AddDataPage() {
  const toast = useToast();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const taskId = searchParams.get("id");
  const { user } = useAuth();

  const { t: globalTranslation } = useTranslation("translation");
  const { t } = useTranslation("translation", {
    keyPrefix: "dataEntryPlanning",
  });

  const [useTemplate, setUseTemplate] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [scope3Expanded, setScope3Expanded] = useState(false);
  const [scopeError, setScopeError] = useState("");

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
    setValue,
  } = useForm({});

  const { data: organizationStructure } = useGetOrganizationStructure();

  const { data: existingTask, isLoading: isTaskLoading } = useGetQuestionnaireById(taskId, {
    enabled: !!taskId,
    cacheTime: 0,
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
  });

  useGetAssigneeTemplates({
    onSuccess: (data) => {
      setTemplates(data);
    },
    cacheTime: 0,
  });

  const formValues = watch();

  const getDimensionsQueryParams = () => {
    const queryParams = {};
    dimensionsKeys.forEach((key) => {
      if (typeof formValues[key] !== "undefined" && formValues[key] !== ALL_VALUE) {
        queryParams[`${key}Item`] = formValues[key];
      }
    });
    return queryParams;
  };

  const { data: dimensionsData } = useGetDimensions(getDimensionsQueryParams(), {
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    cacheTime: 0,
  });

  useEffect(() => {
    if (taskId && existingTask) {
      reset(mapDataToFormData(existingTask));
      const hasScope3 = SCOPE3_CATEGORIES.some((cat) => existingTask?.configurations?.[cat.field]);
      setScope3Expanded(hasScope3);
    } else if (!taskId) {
      reset(mapDataToFormData());
    }
  }, [reset, taskId, existingTask]);

  const handleTemplateChange = (e) => {
    const templateId = e.target.value;
    if (templateId) {
      const template = templates.find((t) => t.id === Number(templateId) || t.id === templateId);
      if (template) {
        setValue("dimensionOne", template?.dimensionItems?.dimensionOneItem || ALL_VALUE);
        setValue("dimensionTwo", template?.dimensionItems?.dimensionTwoItem || ALL_VALUE);
        setValue("dimensionThree", template?.dimensionItems?.dimensionThreeItem || ALL_VALUE);
        setValue("dimensionFour", template?.dimensionItems?.dimensionFourItem || ALL_VALUE);
        setValue("dimensionFive", template?.dimensionItems?.dimensionFiveItem || ALL_VALUE);
        setValue("assignee", template.assigneeId);
      }
    }
  };

  const { data: users, isLoading: isUsersLoading } = useGetOrganizationUsers({
    onError: (err) => {
      showErrorToast(toast, err.message);
    },
    onSettled: () => dispatch(hideAppSpinner()),
    cacheTime: 0,
  });

  useEffect(() => {
    if (isUsersLoading) {
      dispatch(showAppSpinner());
    }
  }, [dispatch, isUsersLoading]);

  useEffect(() => {
    if (users && user?.email && !taskId) {
      const currentUser = users.find((u) => u.email === user.email);
      if (currentUser) {
        setValue("assignee", currentUser.id);
      }
    }
  }, [users, user, taskId, setValue]);

  const goBack = () => navigate(routes.dataCollectionDataEntryPlanning);

  const { mutate: create } = useCreateQuestionnaire({
    onSuccess: (response) => {
      showSuccessToast(toast, t("messages.taskCreated"));
      queryClient.invalidateQueries(organizationQuestionnaireKeys.allDataEntryPlanningQuestionnaires());
      queryClient.invalidateQueries(organizationAdministrationKeys.user());
      dispatch(hideAppSpinner());
      navigate(`${routes.organisationQuestionnaire}?id=${response?.id}`);
    },
    onError: (err) => {
      dispatch(hideAppSpinner());
      showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message);
    },
    onSettled: () => dispatch(hideAppSpinner()),
  });

  const { mutate: update } = useUpdateQuestionnaire({
    onSuccess: () => {
      showSuccessToast(toast, t("messages.taskUpdated"));
      queryClient.invalidateQueries(organizationQuestionnaireKeys.allDataEntryPlanningQuestionnaires());
      dispatch(hideAppSpinner());
      goBack();
    },
    onError: (err) => {
      dispatch(hideAppSpinner());
      showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message);
    },
    onSettled: () => dispatch(hideAppSpinner()),
  });

  const hasScope1or2 = formValues.directEmissionsShouldBeFilled || formValues.indirectEmissionsShouldBeFilled;
  const hasScope3Sub = SCOPE3_CATEGORIES.some((cat) => formValues[cat.field]);
  const isFormValid = !!formValues.assignee && !!formValues.year
    && !!formValues.quarter
    && (hasScope1or2 || hasScope3Sub)
    && (!scope3Expanded || hasScope3Sub);

  const prepareSubmitData = (data) => {
    const hasScope1or2 = data.directEmissionsShouldBeFilled || data.indirectEmissionsShouldBeFilled;
    const hasScope3Sub = SCOPE3_CATEGORIES.some((cat) => data[cat.field]);

    if (!hasScope1or2 && !hasScope3Sub) {
      setScopeError("Please select at least one scope");
      return;
    } else if (scope3Expanded && !hasScope3Sub) {
      setScopeError("Please select at least one Scope 3 category");
      return;
    }
    setScopeError("");

    const quarter = data.quarter;
    const dimensionThreeName = organizationStructure?.dimensions["dimensionThree"]?.items?.find(
      (d) => d?.id === data?.dimensionThree
    )?.name;

    let description;
    if (data.dimensionThree && useTemplate) {
      description = `${data.description || ""} - ${dimensionThreeName}`.trim();
    } else if (data.description?.trim()) {
      description = data.description;
    } else {
      const parts = [];
      if (data.year) parts.push(data.year);
      if (quarter && quarter !== "ALL") parts.push(quarter);
      const dimKeys = [...dimensionsKeys].reverse();
      for (const dk of dimKeys) {
        const dimVal = data[dk];
        if (dimVal && dimVal !== ALL_VALUE) {
          const dim = organizationStructure?.dimensions?.[dk];
          const item = dim?.items?.find((i) => i.id === dimVal || i.id?.toString() === dimVal?.toString());
          if (item) { parts.push(item.name); break; }
        }
      }
      if (parts.length === 1 && data.year) parts.push("All");
      description = parts.join(" · ");
    }
    const dataToSave = {
      description: description,
      dimensionItems: {
        dimensionOneItem:
          data.dimensionOne !== undefined && data.dimensionOne !== ALL_VALUE ? data.dimensionOne : null,
        dimensionTwoItem:
          data.dimensionTwo !== undefined && data.dimensionTwo !== ALL_VALUE ? data.dimensionTwo : null,
        dimensionThreeItem:
          data.dimensionThree !== undefined && data.dimensionThree !== ALL_VALUE ? data.dimensionThree : null,
        dimensionFourItem:
          data.dimensionFour !== undefined && data.dimensionFour !== ALL_VALUE ? data.dimensionFour : null,
        dimensionFiveItem:
          data.dimensionFive !== undefined && data.dimensionFive !== ALL_VALUE ? data.dimensionFive : null,
      },
      assigneeId: data.assignee,
      period: {
        year: data.year,
        quarter,
      },
      configurations: {
        activityDataEnabled: data.activityDataEnabled,
        emissionsDataEnabled: data.emissionsDataEnabled,
        directEmissionsShouldBeFilled: data.directEmissionsShouldBeFilled,
        indirectEmissionsShouldBeFilled: data.indirectEmissionsShouldBeFilled,
        purchasedGoodsAndServicesShouldBeFilled: data.purchasedGoodsAndServicesShouldBeFilled,
        capitalGoodsShouldBeFilled: data.capitalGoodsShouldBeFilled,
        fuelAndEnergyRelatedActivitiesShouldBeFilled: data.fuelAndEnergyRelatedActivitiesShouldBeFilled,
        upstreamTransportationAndDistributionShouldBeFilled:
          data.upstreamTransportationAndDistributionShouldBeFilled,
        wasteGeneratedInOperationsShouldBeFilled: data.wasteGeneratedInOperationsShouldBeFilled,
        businessTravelShouldBeFilled: data.businessTravelShouldBeFilled,
        employeeCommutingShouldBeFilled: data.employeeCommutingShouldBeFilled,
        upstreamLeasedAssetsShouldBeFilled: data.upstreamLeasedAssetsShouldBeFilled,
        downstreamTransportationAndDistributionShouldBeFilled:
          data.downstreamTransportationAndDistributionShouldBeFilled,
        processingOfSoldProductsShouldBeFilled: data.processingOfSoldProductsShouldBeFilled,
        useOfSoldProductsShouldBeFilled: data.useOfSoldProductsShouldBeFilled,
        treatmentOfSoldProductsShouldBeFilled: data.treatmentOfSoldProductsShouldBeFilled,
        downstreamLeasedAssetsShouldBeFilled: data.downstreamLeasedAssetsShouldBeFilled,
        franchisesShouldBeFilled: data.franchisesShouldBeFilled,
        investmentsShouldBeFilled: data.investmentsShouldBeFilled,
      },
    };
    dispatch(showAppSpinner());
    if (taskId) {
      update({ id: taskId, data: dataToSave });
    } else {
      create(dataToSave);
    }
  };

  const renderDimensionOptions = (dimKey) => {
    const dimItems = [];
    if (dimensionsData[dimKey] && dimensionsData[dimKey].items && dimensionsData[dimKey].items.length > 0) {
      dimItems.push(...dimensionsData[dimKey].items);
    }
    const currentValue = formValues[dimKey];
    const filtered = filterActiveDimensionItems(dimItems, currentValue);
    return [{ id: ALL_VALUE, name: "All" }, ...filtered].map((item) => (
      <option key={`${dimKey}-${item.id}`} value={item.id}>
        {item.name}
      </option>
    ));
  };

  if (!organizationStructure || !dimensionsData || isUsersLoading || (taskId && isTaskLoading)) {
    return <LocalSpinner />;
  }

  return (
    <Stack spacing={10} p={4} bg="pureWhite" maxW="620px" mx="auto">
      <Box>
        <Heading fontSize="48px" fontWeight="700" color="darkTeal">
          {taskId ? t("modal.editTaskHeader") : "Add Data"}
        </Heading>
        <Text fontSize="16px" color="gray.500" maxW="75%">
          Add data to calculate GHG emissions
        </Text>
      </Box>

      <chakra.form onSubmit={handleSubmit(prepareSubmitData)}>
        <VStack
          spacing={2}
          align="stretch"
          sx={{
            "select, textarea": {
              borderRadius: "xl", fontSize: "14px", h: "42px", px: 4,
              borderColor: "gray.300",
              _focus: { borderColor: "lime", boxShadow: "0 0 0 1px var(--chakra-colors-lime)" },
              _hover: { borderColor: "gray.400" },
            },
            "textarea": { borderRadius: "xl", h: "42px", py: 2, resize: "none", overflow: "hidden" },
          }}
        >
          {/* Added by */}
          <Box py={2}>
            <Text fontSize="18px" fontWeight="700" color="gray.800" mb={3}>Added by</Text>
            <FormSelect
              placeholder=" "
              isDisabled
              errors={errors}
              maxInlineSize="full"
              {...register("assignee", {
                required: globalTranslation("entityRequired", { entityName: "Added by" }),
              })}
            >
              {users
                ? sortUsersByEmail(users).map((assignee) => (
                    <option key={assignee.id} value={assignee.id}>
                      {assignee.email}
                    </option>
                  ))
                : []}
            </FormSelect>
          </Box>

          {/* Period */}
          <Box py={2}>
            <Text fontSize="18px" fontWeight="700" color="gray.800" mb={1}>Period</Text>
            <Text fontSize="13px" color="gray.500" mb={3}>Select the financial year and quarter for this task</Text>
            <Flex gap={4}>
              <FormSelect
                label={`${t("modal.financialYear")} *`}
                type="text"
                errors={errors}
                maxInlineSize="full"
                {...register("year", {
                  required: globalTranslation("entityRequired", { entityName: t("modal.financialYear") }),
                })}
              >
                {generateArrayOfYears().map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </FormSelect>

              <FormSelect
                label={`${t("modal.quarter")} *`}
                errors={errors}
                maxInlineSize="full"
                {...register("quarter", {
                  required: globalTranslation("entityRequired", { entityName: t("modal.quarter") }),
                })}
              >
                <option value="">{" "}</option>
                <option value="ALL">{t("modal.quarterAll")}</option>
                <option value="Q1">{`${t("modal.quarterLetter")}1`}</option>
                <option value="Q2">{`${t("modal.quarterLetter")}2`}</option>
                <option value="Q3">{`${t("modal.quarterLetter")}3`}</option>
                <option value="Q4">{`${t("modal.quarterLetter")}4`}</option>
              </FormSelect>
            </Flex>
          </Box>

          {/* Dimensions */}
          <Box py={2}>
            <Flex justify="space-between" align="center" mb={1}>
              <Text fontSize="18px" fontWeight="700" color="gray.800">Dimensions</Text>
              <FormControl display="flex" alignItems="center" w="auto" gap={2}>
                <FormLabel htmlFor="use-template" mb="0" fontSize="13px" color="gray.500" whiteSpace="nowrap">
                  {t("modal.useTemplate")}
                </FormLabel>
                <Switch
                  id="use-template"
                  colorScheme="limeShade"
                  size="sm"
                  isChecked={useTemplate}
                  onChange={(e) => setUseTemplate(e.target.checked)}
                />
              </FormControl>
            </Flex>
            <Text fontSize="13px" color="gray.500" mb={3}>Select the organizational dimensions for this task</Text>
            <Flex direction="column" gap={3}>
              {useTemplate && (
                <Box>
                  <FormSelect label={t("modal.selectTemplate")} onChange={handleTemplateChange} maxInlineSize="full">
                    <option value="">{t("modal.selectTemplateOption")}</option>
                    {templates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </FormSelect>
                </Box>
              )}

              {dimensionsKeys.map((dimKey) => {
                if (organizationStructure.dimensions[dimKey]) {
                  return (
                    <FormSelect
                      {...register(`${dimKey}`)}
                      key={`${dimKey}-select`}
                      label={`${organizationStructure.dimensions[dimKey].name} *`}
                      errors={errors}
                      disabled={(existingTask && existingTask.status !== QUESTIONNAIRE_STATUSES.NEW) || useTemplate}
                      maxInlineSize="full"
                    >
                      {renderDimensionOptions(dimKey)}
                    </FormSelect>
                  );
                }
                return null;
              })}
            </Flex>
          </Box>

          {/* Data */}
          <Box py={2}>
            <Text fontSize="18px" fontWeight="700" color="gray.800" mb={4}>Data</Text>
            <EsgFormLabel>Scopes</EsgFormLabel>
            <Text fontSize="13px" color="gray.500" mb={2}>Choose which emission scopes to collect data for</Text>
            <Wrap spacing={2} mb={3}>
              <ScopeTag
                label="Scope 1"
                isSelected={!!formValues.directEmissionsShouldBeFilled}
                onClick={() => setValue("directEmissionsShouldBeFilled", !formValues.directEmissionsShouldBeFilled)}
              />
              <ScopeTag
                label="Scope 2"
                isSelected={!!formValues.indirectEmissionsShouldBeFilled}
                onClick={() => setValue("indirectEmissionsShouldBeFilled", !formValues.indirectEmissionsShouldBeFilled)}
              />
              <ScopeTag
                label="Scope 3"
                isSelected={scope3Expanded}
                onClick={() => {
                  const next = !scope3Expanded;
                  setScope3Expanded(next);
                  if (!next) {
                    SCOPE3_CATEGORIES.forEach((cat) => setValue(cat.field, false));
                  }
                }}
              />
            </Wrap>
            {scopeError && (
              <Text fontSize="13px" color="red.500" mt={1}>{scopeError}</Text>
            )}
            {scope3Expanded && (
              <Stack pl={2} spacing={2} mt={2}>
                {SCOPE3_CATEGORIES.map((cat) => (
                  <Checkbox
                    key={cat.field}
                    colorScheme="limeShade"
                    isChecked={!!formValues[cat.field]}
                    isDisabled={cat.disabled}
                    onChange={(e) => setValue(cat.field, e.target.checked)}
                    size="lg"
                    sx={{ ".chakra-checkbox__control": { borderRadius: "md" } }}
                  >
                    <Text fontSize="14px" color={cat.disabled ? "gray.400" : "gray.700"}>{cat.label}</Text>
                  </Checkbox>
                ))}
              </Stack>
            )}
          </Box>

          {/* Data Type */}
          <Box py={2}>
            <EsgFormLabel>Data Type</EsgFormLabel>
            <Text fontSize="13px" color="gray.500" mb={2}>Choose the type of data to collect</Text>
            <Flex gap={6}>
              <FormControl display="flex" alignItems="center" w="auto" gap={2}>
                <Switch
                  id="activity-data"
                  colorScheme="limeShade"
                  isChecked={!!formValues.activityDataEnabled}
                  onChange={(e) => setValue("activityDataEnabled", e.target.checked)}
                />
                <FormLabel htmlFor="activity-data" mb="0" fontSize="14px" fontWeight="600" color={formValues.activityDataEnabled ? "gray.800" : "gray.400"}>
                  Activity Data
                </FormLabel>
              </FormControl>
              <FormControl display="flex" alignItems="center" w="auto" gap={2}>
                <Switch
                  id="emissions-data"
                  colorScheme="limeShade"
                  isChecked={!!formValues.emissionsDataEnabled}
                  onChange={(e) => setValue("emissionsDataEnabled", e.target.checked)}
                />
                <FormLabel htmlFor="emissions-data" mb="0" fontSize="14px" fontWeight="600" color={formValues.emissionsDataEnabled ? "gray.800" : "gray.400"}>
                  Emissions Data
                </FormLabel>
              </FormControl>
            </Flex>
          </Box>

          {/* Description */}
          <Box py={2}>
            <Text fontSize="18px" fontWeight="700" color="gray.800" mb={1}>Description</Text>
            <Text fontSize="13px" color="gray.500" mb={3}>Leave blank for auto-generation or add a custom description for the task.</Text>
            <FormTextArea type="text" errors={errors} {...register("description")} blockSize="auto" rows={1} controlProps={{ maxInlineSize: "full" }} />
          </Box>

          <Flex gap={6} pt={6} pb={8}>
            <Button
              onClick={goBack}
              flex={1}
              h="56px"
              borderRadius="xl"
              fontSize="18px"
              bg="transparent"
              color="pureBlack"
              border="1px solid"
              borderColor="gray.300"
              _hover={{ bg: "gray.50" }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              flex={1}
              h="56px"
              borderRadius="xl"
              fontSize="18px"
              bg="lime"
              color="pureBlack"
              _hover={{ bg: "limeShade.300" }}
              isDisabled={!isFormValid}
            >
              {taskId ? t("modal.save") : "Add Data"}
            </Button>
          </Flex>
        </VStack>
      </chakra.form>
    </Stack>
  );
}

export default AddDataPage;
